import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, lastValueFrom, Observable, of, Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { AutomationSource } from './automationsSource';
import {
  AutomationTemplate,
  SelectedAutomation
} from '../../models/discover/automation-template.model';
import { z } from 'zod';
import { DiscoverConstants } from 'spotlight-ui/app/discover/discover.constants';
import { catchError, map } from 'rxjs/operators';
import {
  Automation,
  AutomationCustomerCount,
  CombinedAutomationDetails
} from 'spotlight-ui/app/models/discover/automation.model';
import { CampaignDetails, CampaignType, Notification } from 'spotlight-ui/app/models/engage/campaign-details.model';
import {
  DigitalMarketingTemplateSource,
  TemplateSource
} from 'spotlight-ui/app/engage/dependencies/DigitalMarketingTemplateSource';
import moment from 'moment-timezone';
import { CampaignSource } from 'spotlight-ui/app/engage/dependencies/ICampaignSource';
import { DigitalMarketingCampaignSource } from 'spotlight-ui/app/engage/dependencies/DigitalMarketingCampaignSource';
import { Step } from 'spotlight-ui/app/shared/progress-stepper/progress-stepper.component';

export enum ActionType {
  Stop = 'STOPPED',
  Create= 'CREATED',
  Edit = 'UPDATED',
}

@Injectable({
  providedIn: 'root',
})
export class DigitalMarketingAutomationSource implements AutomationSource {

  constructor(
      private readonly httpClient: HttpClient
  ) {
  }

  private readonly templateSource: TemplateSource = new DigitalMarketingTemplateSource(this.httpClient);
  private readonly campaignSource: CampaignSource = new DigitalMarketingCampaignSource(this.httpClient);

  public getAutomationTemplate(locationNumber: string, tileId: string): Observable<AutomationTemplate> {
    const automationTemplate: Subject<any> = new Subject<any>();
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    this.httpClient.get(environment.digitalMarketingCampaignsServiceURL +
        '/api/restaurant/templates/automations/' + tileId, { headers: headers })

      .subscribe({
        next: (response) => {
          const result = AutomationTemplateDetailsSchema.safeParse(response);
          if (!result.success) {
            automationTemplate.error(new Error(`Invalid response format for ${tileId}`));
            return;
          }
          this.getCampaignTemplateDetails( locationNumber, result.data as AutomationTemplate,).subscribe({
            next: (updatedTemplate) => {
              automationTemplate.next({
                ...updatedTemplate,
                display: {
                  ...updatedTemplate.display,
                  automationCancellationTitle: `This will STOP automation of the ${updatedTemplate.display.title} reward`,
                  automationCancellationBody: `You can re-activate this automation at any time through discover`,
                }
              });
              automationTemplate.complete();
            },
            error: (error) => {
              automationTemplate.error(error);
              return of();
            }
          })
        },
        error: (error) => {
          automationTemplate.error(error.error);
          automationTemplate.complete();
        }
      });
    return automationTemplate.asObservable();
  }

  public createAutomation(locationNumber: string, automationDetails: SelectedAutomation): Observable<any> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const automationID: Subject<any> = new Subject<any>();
    const automationPayload = this.convertAutomationToPayload(automationDetails)
    this.httpClient.post(environment.digitalMarketingCampaignsServiceURL +
        '/api/restaurant/automations/campaigns', automationPayload, { headers: headers })
      .subscribe({
        next: (response) => {
          const result = z.object({ id: z.string() }).safeParse(response);
          if (!result.success) {
            automationID.error(new Error(`No ID received for automated campaign`));
            return;
          }
          automationID.next(result.data.id);
          automationID.complete();
        },
        error: (error) => {
          automationID.error(error.error);
        }
      });
    return automationID.asObservable();
  }

  public updateAutomation(
    locationNumber: string,
    id: string,
    automationDetails: SelectedAutomation
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const automationID: Subject<any> = new Subject<any>();
    const automationPayload = this.convertAutomationToPayload(automationDetails)
    this.httpClient.patch(environment.digitalMarketingCampaignsServiceURL +
        '/api/restaurant/automations/campaigns/' + id, automationPayload, { headers: headers })
      .subscribe({
        next: (response) => {
          automationID.next(id);
          automationID.complete();
        },
        error: (error) => {
          automationID.error(error.error ?? error);
          automationID.complete();
        }
      });
    return automationID.asObservable();
  }

  public getAutomationById(locationNumber: string, id: string): Observable<CombinedAutomationDetails> {
    const automation: Subject<any> = new Subject<any>();
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    this.httpClient.get(environment.digitalMarketingCampaignsServiceURL +
        '/api/restaurant/automations/campaigns/' + id , { headers: headers })
      .subscribe({
        next: (response) => {
          const result = AutomationDetailsSchema.safeParse(response);
          if (!result.success) {
            automation.error(new Error(`Invalid response format for ${id}`));
            return;
          }
          this.getAutomationTemplate(locationNumber, result.data.automationTemplateID).subscribe({
            next: template => {
              automation.next({
                template: template,
                automationDetails: {
                  ...result.data,
                  automationID: result.data.id
                }
              });
              automation.complete();
            },
            error: (error) => {
              automation.error(error?.error ?? error);
            }
          })
        },
        error: (error) => {
          automation.error(error.error);
        }
      });
    return automation.asObservable();
  }

  public getAutomationByIdAllDetails(locationNumber: string, id: string):
      Observable<CombinedAutomationDetails> {
    const automation: Subject<any> = new Subject<any>();
    this.getAutomationById(locationNumber, id).subscribe({
      next: details => {
        let automationDetails = details.automationDetails;
        let template = details.template;
        if (automationDetails.campaignIDs?.length > 0) {
          this.handleAutomatedCampaignsDetails(locationNumber, automationDetails).subscribe({
            next: automationDetailsWithCampaigns => {
              automation.next({ automationDetails: automationDetailsWithCampaigns, template, locationNumber });
              automation.complete();
            }, error: error => {
              automation.error(error)
            }
          })
        } else {
          automationDetails.progressStepper = [
            { stepName: 'Scheduled', isCompleted: true },
            { stepName: 'Reward Processed', isCompleted: false },
            { stepName: 'Email Processed', isCompleted: false },
          ];
          automation.next({ automationDetails, template, locationNumber })
          automation.complete()
        }
      },
      error: error => {
        automation.error(error.error)
      }
    })
    return automation.asObservable();
  }

  public getMappedAutomationTiles(locationNumber: string, featureMap): Observable<any[]> {
    const mappedAutomations = new Subject<any[]>();
    forkJoin([
      this.getAllAutomationTemplates(locationNumber),
      this.getActiveAutomations(locationNumber),
      this.getEnabledAutomations(locationNumber)
    ]).subscribe({
      next: ([templates, activeAutomationIDs, enabledAutomations] : [AutomationTemplate[], Automation[], string[]]) => {
        mappedAutomations.next(this.formatTiles(templates, activeAutomationIDs, enabledAutomations, featureMap));
        mappedAutomations.complete();
      },
      error: (error) => {
        mappedAutomations.error(error);
      }
    })
    return mappedAutomations.asObservable();
  }

  public getAutomationAudience(locationNumber: string, id: string): Observable<AutomationCustomerCount> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    return this.httpClient.get(environment.digitalMarketingCampaignsServiceURL +
        `/api/restaurant/automations/campaigns/${id}/customers`, { headers }).pipe(
      map((response) => {
        const result = z.array(z.string()).safeParse(response);
        if (!result.success) {
          throw new Error(`Invalid response format for ${id}`);
        }
        return {
          id: DiscoverConstants.automationToTileIdsMapping[id],
          count: result.data.length,
          customers: result.data,
          disableEngagement: result.data.length === 0
        };
      })
    );
  }

  public stopAutomation(locationNumber: string, tileId: string, id: string): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const stoppedAutomationID = new Subject<any>()
    this.httpClient.delete(environment.digitalMarketingCampaignsServiceURL +

        '/api/restaurant/automations/campaigns/' + id, { headers: headers }).subscribe({
      next:()=> {
        stoppedAutomationID.next(id)
        stoppedAutomationID.complete();
      },
      error: (error) => {
        stoppedAutomationID.error(error);
        stoppedAutomationID.complete();
      }
    })
    return stoppedAutomationID.asObservable()
  }

  public fetchAutomations(payload: any, locationNumber: string): Observable<Automation[]> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const automationCampaignsUrl = `${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/automations/campaigns`
        + `?startDate=${payload.fromDate}T00:00:00Z&endDate=${payload.toDate}T23:59:00Z`;
    return this.httpClient.get(automationCampaignsUrl, { headers }).pipe(
      map((automations: Automation[]) => automations),
      catchError(()=> of([]))
    );
  }

  private getCampaignTemplateDetails(locationNumber: string, res: AutomationTemplate): Observable<AutomationTemplate> {
    const campaignTemplateDetails$ = res.campaignTemplates.map((campaignTemplate) =>
      (this.templateSource.getTemplateDetails(locationNumber, campaignTemplate.campaignTemplateID).pipe(
        map((template) => {
          let notificationLayout = template.notificationLayouts[0];
          notificationLayout.themes = notificationLayout.themes.filter((theme) => theme.id === campaignTemplate.themeID);
          return {
            ...template,
            notificationLayouts: [notificationLayout],
          }
        })
      )
      ));

    return new Observable<AutomationTemplate>((observer) => {
      Promise.all(campaignTemplateDetails$.map((obs) => lastValueFrom(obs)))
        .then((campaignTemplateDetails) => {
          res['campaignTemplateDetails'] = campaignTemplateDetails;
          observer.next(res);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  private handleAutomatedCampaignsDetails(locationNumber, automationDetails): Observable<Automation> {
    const automation: Subject<any> = new Subject<any>();
    forkJoin(automationDetails.campaignIDs.map((id) =>
      this.campaignSource.getCampaignDetails(locationNumber, id)
    )).subscribe({
      next: (campaigns: CampaignDetails[]) => {
        automationDetails.campaigns = campaigns;
        campaigns.sort((a, b) => (moment(a.createdAt).isBefore(moment(b.createdAt)) ? 1 : -1));
        const latestCampaign = campaigns[0];
        automationDetails.progressStepper = this.mapProgressStepper(latestCampaign);
        automation.next(automationDetails)
        automation.complete();
      },
      error: error =>{
        automation.error(error)
        automation.complete();
      }
    });
    return automation.asObservable();
  }

  private mapProgressStepper(campaignDetails: CampaignDetails) {
    const sendProgress: Step[] = [
      {
        stepName: 'Scheduled',
        isCompleted: true,
      }
    ]
    if (campaignDetails.reward) {
      const isRewardSent: boolean = campaignDetails.reward.offerBatchID !== undefined
          || campaignDetails.notifications[0].creationStatus == 'COMPLETED'
      const rewardSendFailure: string = !isRewardSent && campaignDetails.status == 'FAILED'
        ? 'Reward processing failed. No rewards were loaded into customer accounts for ' +
          'the latest campaign launched by this automation.' : null
      sendProgress.push({ stepName: 'Reward Processed', isCompleted: isRewardSent, error: rewardSendFailure })
    }
    campaignDetails.notifications.forEach((notification: Notification) => {
      const isNotificationSent: boolean = notification.creationStatus == 'COMPLETED'
      const notificationSendFailure: string = notification.creationStatus == 'FAILED' && campaignDetails.status == 'FAILED'
        ? 'Email processing failed. No attempts could be made to send emails for the latest campaign launched by ' +
          'this automation.' : null
      sendProgress.push({ stepName: 'Email Processed', isCompleted: isNotificationSent, error: notificationSendFailure })
    })
    return sendProgress;
  }

  private getAllAutomationTemplates(locationNumber) {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const automationCampaignsUrl = `${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/templates/automations`;
    const allAutomationTemplates: Subject<AutomationTemplate[]> = new Subject();
    this.httpClient.get(automationCampaignsUrl, { headers }).subscribe({
      next :(automationTemplates: any[]) => {
        automationTemplates = automationTemplates.map( (template) =>{
          let result = AutomationTemplateDetailsSchema.safeParse(template);
          if (!result.success) {
            return {
              ...template,
              error: `Invalid response format for template ${template.id}`
            };
          }
          return result.data;
        })
        allAutomationTemplates.next(automationTemplates);
        allAutomationTemplates.complete()
      },
      error: ()=> {
        allAutomationTemplates.next([]);
        allAutomationTemplates.complete()
      }
    });
    return allAutomationTemplates.asObservable();
  }

  private getActiveAutomations(locationNumber): Observable<Automation[]> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const activeAutomationIDs = new Subject<Automation[]>();
    const automationCampaignsUrl = `${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/automations/campaigns`
        + `?status=ACTIVE`;
    this.httpClient.get(automationCampaignsUrl, { headers }).subscribe({
      next :(automations: Automation[]) => {
        activeAutomationIDs.next(automations);
        activeAutomationIDs.complete();
      },
      error: (error)=> {
        activeAutomationIDs.next([]);
        activeAutomationIDs.complete();
      }
    });
    return activeAutomationIDs.asObservable();
  }

  private getEnabledAutomations(locationNumber): Observable<string[]> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const enabledAutomationIDs = new Subject<string[]>();
    const automationCampaignsUrl = `${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/automations/capabilities`
    this.httpClient.get(automationCampaignsUrl, { headers }).subscribe({
      next: (automations) => {
        const result = z.array(z.string()).safeParse(automations);
        enabledAutomationIDs.next(result.success ? result.data : []);
        enabledAutomationIDs.complete();
      },
      error: (error)=> {
        enabledAutomationIDs.next( []);
        enabledAutomationIDs.complete();
      }
    });
    return enabledAutomationIDs.asObservable();
  }

  private formatTiles(templates: AutomationTemplate[], activeAutomationIDs, enabledAutomationIDs, featureMap) {
    let tiles = []
    templates.forEach(template => {
      let tileId = DiscoverConstants.automationToTileIdsMapping[template.id];
      let enabled = enabledAutomationIDs?.includes(template.id);
      if (featureMap[tileId] && !template.error && enabled ) {
        let active = activeAutomationIDs?.find((automation) => automation.automationTemplateID === template.id);
        let tileAutomation = template.id !== 'recognize_catering'; // the one thats not automated
        tiles.push({
          automated: active != undefined,
          automationEnabled: tileAutomation,
          enabled: 'true',
          id: tileId,
          fieldId: active?.id ?? undefined,
          category: 'filter',
          type: 'stat',
          dataType:'integer',
          sendTreat: template.campaignTemplates.some(campaignTemplate =>
            campaignTemplate.campaignTemplateID.includes('reward')
          ),
          sendEmail: template.campaignTemplates.some(campaignTemplate =>
            campaignTemplate.campaignTemplateID.includes('notification')
          ),
          stayInPlaceAfterAutomating: false,
          imageUrl: template.display.imageUrl,
          title: template.display.title,
          helpText: {
            audienceText: template.display.audience.selectionDescription,
            filterText: template.display.audience.filters,
            strategicGuidanceText: template.display.audience.guidance,
          },
          automationCancellationTitle: `This will STOP automation of the ${template.display.title} reward`,
          automationCancellationBody: `You can re-activate this automation at any time through discover`,
          descriptionText: {
            primary: template.display.audience.countDescription
          },
          ...(template.goal ? { goal: template.goal } : {})
        })
      }
    })

    return tiles;
  }

  private convertAutomationToPayload(selectedAutomation: SelectedAutomation) {
    return {
      automationTemplateID: selectedAutomation.templateID,
      name: selectedAutomation.customizations.name,
      emailCustomizations: {
        subject: selectedAutomation.customizations.subject,
        body: selectedAutomation.customizations.body,
        themeID: selectedAutomation.customizations.themeID,
        heroImage: selectedAutomation.customizations.heroImage,
      },
      campaignType: CampaignType.Reward,
      campaignTemplateID: selectedAutomation.automationCampaignDetails.selectedCampaignTemplateID,
      reward: {
        durationDays: selectedAutomation.automationCampaignDetails.durationDays,
        includeActiveLocationRewardCustomers: selectedAutomation.automationCampaignDetails.includeActiveLocationRewardCustomers,
        appMessage: selectedAutomation.automationCampaignDetails.appMessage
      }
    }
  }

}

export const DisplaySchema= z.object({
  title: z.string(),
  subtitle: z.string(),
  imageUrl: z.string(),
  audience: z.object({
    filters: z.array(z.string()),
    guidance: z.string(),
    countDescription: z.string(),
    selectionDescription: z.string()
  }),
  confirmation: z.string()
})

export const AutomationTemplateDetailsSchema = z.object({
  id: z.string(),
  query: z.any(),
  launchTime: z.string(),
  campaignTemplates: z.array(z.object({
    campaignTemplateID: z.string(),
    themeID: z.string()
  })),
  display: DisplaySchema,
  goal: z.string().nullish()
});

export const AutomationDetailsSchema= z.object({
  id: z.string(),
  automationTemplateID: z.string(),
  name: z.string(),
  customizations: z.object({
    subject: z.string(),
    body: z.string(),
    themeID: z.string(),
    heroImage: z.object({
      desktopURL: z.string(),
      mobileURL: z.string()
    }),
    recruitmentUrl: z.string().optional()
  }),
  campaignType: z.string(),
  campaignTemplateID: z.string(),
  reward: z.object({
    durationDays: z.number(),
    includeActiveLocationRewardCustomers: z.boolean(),
    appMessage: z.string()
  }).optional(),
  campaignIDs: z.array(z.string()),
  createdBy: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().optional(),
})

